import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { setModal } from '../../slices/modalSlice'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import * as datosFiscalesService from '../servicios/datosFiscalesService'
import * as domiciliosService from '../servicios/domiciliosService'
import * as usuarioService from '../servicios/usuarioService'
import styles from './DatosFiscales.module.css'

const DatosFiscales = () => {
  const [datosFiscales, setDatosFiscales] = useState([])
  const [provincias, setProvincias] = useState([])
  const [tiposFiscales, setTiposFiscales] = useState([])

  const { loginDataContext: { idUsuario, empid } = {}, esPerfilAdmin } = useAuth()

  const dispatch = useDispatch()

  const { values, errores: erroresModal, editMode } = useSelector((state) => state.genericModal)

  const obtenerDatosFiscales = async () => {
    const { data } = await datosFiscalesService.buscarTodos(empid)
    setDatosFiscales(data)
  }

  const obtenerTiposFiscales = async () => {
    const { data } = await usuarioService.obtenerTiposFiscales()
    setTiposFiscales(data.map((t) => ({ value: t.idCondicionFrenteIIBB, label: t.tipo })))
  }

  const obtenerProvincias = async () => {
    const { data } = await domiciliosService.BuscarPorPais(1)
    setProvincias(data)
  }

  const campos = [
    {
      type: 'select',
      name: 'provincia',
      placeholder: 'Seleccione una',
      label: 'Provincia',
      isFullWidth: false,
      isEditable: false,
      isGrid: false,
      options: provincias.map((p) => ({
        value: p.idProvincia,
        label: p.nombre,
        key: p.idProvincia
      }))
    },
    {
      type: 'select',
      name: 'condicionFrenteIIBB',
      placeholder: 'Seleccionar',
      label: 'Condición frente a IIBB',
      isFullWidth: false,
      isEditable: true,
      isGrid: false,
      options: tiposFiscales.map((t) => ({
        value: t.value,
        label: t.label,
        key: t.value
      }))
    },
    {
      type: 'select',
      name: 'esAgente',
      placeholder: 'Seleccionar',
      label: 'Es agente de percepción',
      isFullWidth: false,
      isEditable: true,
      isGrid: false,
      options: [
        { value: 'true', label: 'Sí', key: 'agente-true' },
        { value: 'false', label: 'No', key: 'agente-false' }
      ]
    }
  ]

  const abrirModal = () => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Agregar dato fiscal',
        type: 'form',
        body: '',
        show: true,
        editMode: false,
        values: {
          provincia: '',
          condicionFrenteIIBB: '',
          esAgente: ''
        },
        campos
      })
    )
  }

  const guardarDatoFiscal = async () => {
    try {
      const nuevoDatoFiscal = {
        idUsuario,
        idProvincia: parseInt(values.provincia),
        idCondicionFrenteIIBB: parseInt(values.condicionFrenteIIBB),
        esAgentePercepcion: values.esAgente === 'true',
        empid: empid
      }

      await datosFiscalesService.insertar(nuevoDatoFiscal)
      obtenerDatosFiscales()
    } catch (error) {
      const mensaje = `Ocurrió un error al guardar el dato fiscal.`
      toastErrorLog(error, mensaje, 'datosFiscales')
      const mensajeError =
        error.response?.data?.message || error.response?.data || 'Error al guardar el dato fiscal.'

      const errorFinal =
        typeof mensajeError === 'string' ? mensajeError : 'Error al guardar el dato fiscal.'

      dispatch(
        setModal({
          errores: { general: errorFinal },
          type: 'form'
        })
      )
      throw error
    }
  }

  const eliminarDatoFiscal = async (idDatoFiscal) => {
    try {
      await datosFiscalesService.eliminar(idDatoFiscal)
      obtenerDatosFiscales()
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el dato fiscal.`
      toastErrorLog(error, mensaje, 'datosFiscales')
    }
  }

  const handleActualizar = async () => {
    if (
      datosFiscales.find(
        (d) =>
          d.idProvincia === parseInt(values.provincia) &&
          d.idCondicionFrenteIIBB === parseInt(values.condicionFrenteIIBB) &&
          d.esAgentePercepcion === (values.esAgente === 'true') &&
          (!editMode || d.idDatoFiscal !== values.idDatoFiscal)
      )
    ) {
      const mensajeError =
        'Ya existe una percepción para la provincia, condición y agente ingresados.'

      dispatch(
        setModal({
          errores: { general: mensajeError },
          type: 'form'
        })
      )
      throw new Error(mensajeError)
    }
    try {
      const datoActualizado = {
        idDatoFiscal: values.idDatoFiscal,
        idCondicionFrenteIIBB: parseInt(values.condicionFrenteIIBB),
        esAgentePercepcion: values.esAgente === 'true'
      }

      await datosFiscalesService.actualizar(datoActualizado)
      obtenerDatosFiscales()
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar el dato fiscal.`
      toastErrorLog(error, mensaje, 'datosFiscales')

      const mensajeError =
        error.response?.data?.message ||
        error.response?.data ||
        'Error al actualizar el dato fiscal.'

      const errorFinal =
        typeof mensajeError === 'string' ? mensajeError : 'Error al actualizar el dato fiscal.'
      dispatch(
        setModal({
          errores: { general: errorFinal },
          type: 'form'
        })
      )
      throw error
    }
  }

  const editarDatoFiscal = (dato) => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Editar dato fiscal',
        type: 'form',
        body: '',
        show: true,
        editMode: true,
        values: {
          idDatoFiscal: dato.idDatoFiscal,
          provincia: dato.idProvincia,
          condicionFrenteIIBB: dato.idCondicionFrenteIIBB,
          esAgente: dato.esAgentePercepcion ? 'true' : 'false'
        },
        campos
      })
    )
  }

  useEffect(() => {
    if (idUsuario) {
      obtenerDatosFiscales()
    }
    obtenerProvincias()
    obtenerTiposFiscales()
  }, [idUsuario])

  const handleChangeModal = (e) => {
    const { name, value } = e.target
    const nuevasValidaciones = { ...erroresModal }

    dispatch(setModal({ values: { ...values, [name]: value } }))

    if (erroresModal[name]) {
      delete nuevasValidaciones[name]
      dispatch(setModal({ errores: nuevasValidaciones }))
    }
  }

  const handleConfirm = async () => {
    const errores = {}

    if (!values.provincia || values.provincia === '') {
      errores.provincia = 'Debe seleccionar una provincia.'
    }
    if (!values.condicionFrenteIIBB || values.condicionFrenteIIBB === '') {
      errores.condicionFrenteIIBB = 'Debe seleccionar una condición frente al IIBB.'
    }
    if (!values.esAgente || values.esAgente === '') {
      errores.esAgente = 'Debe seleccionar si es agente de percepción.'
    }

    const existeDuplicado = datosFiscales.find(
      (d) =>
        d.idProvincia === parseInt(values.provincia) &&
        d.idCondicionFrenteIIBB === parseInt(values.condicionFrenteIIBB) &&
        d.esAgentePercepcion === (values.esAgente === 'true') &&
        (!editMode || d.idDatoFiscal !== values.idDatoFiscal)
    )

    if (existeDuplicado) {
      errores.general =
        'Ya existe ese dato fiscal para la provincia, condición y agente seleccionados.'
    }

    dispatch(setModal({ errores }))

    if (Object.keys(errores).length > 0) {
      return false
    }

    try {
      if (editMode) {
        await handleActualizar()
      } else {
        await guardarDatoFiscal()
      }
      return true
    } catch {
      return false
    }
  }
  return (
    <ul>
      <GenericModal onConfirm={handleConfirm} onChange={handleChangeModal} />
      <div>
        <div className={styles.percepcionesGrid}>
          {datosFiscales.map((dato) => {
            return (
              <div key={dato.idDatoFiscal} className={styles.percepcionCard}>
                <div className={styles.percepcionInfo}>
                  <p>
                    <strong>Provincia: </strong>{' '}
                    {provincias.find((p) => p.idProvincia === dato.idProvincia)?.nombre || ''}
                  </p>
                  <p>
                    <strong>Condición frente al IIBB: </strong>{' '}
                    {tiposFiscales.find((t) => t.value === dato.idCondicionFrenteIIBB)?.label || ''}
                  </p>
                  <p>
                    <strong>Es agente de percepción: </strong>{' '}
                    {dato.esAgentePercepcion ? 'Sí' : 'No'}
                  </p>
                </div>
                {esPerfilAdmin && (
                  <div className={styles.btnContainer}>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                      onClick={() => editarDatoFiscal(dato)}
                      title="Editar percepción"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnOutlineDanger}`}
                      onClick={() => eliminarDatoFiscal(dato.idDatoFiscal)}
                      title="Eliminar percepción"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {esPerfilAdmin && (
          <div className={styles.btnContainer}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutlinePrimary} `}
              onClick={abrirModal}
            >
              Agregar dato fiscal
            </button>
          </div>
        )}
      </div>
    </ul>
  )
}

export default DatosFiscales
